/**
 * The hatch sequence's presentation (ADR 0033, lifted from brad-airship
 * `airship-creatures.js` `updateHatch` and `airship-sprite-art.js`
 * `spriteCard`): at the hatchery's hatch spot an egg card wobbles, cracks and
 * glows, then the hatchling has appeared. The sequence runs on its own
 * elapsed time (the prototype's gotcha: an event-relative hatch clock) on the
 * view's event clock, never on the job's clock nor the ambient motion's
 * (reduced motion stills the wobble, nothing else: the sequence still runs
 * its length); it decides nothing (the job's claim is the shell's).
 *
 * - The egg card: a 1.2 × 1.6 m plane standing 0.8 m up at the spot, facing
 *   the camera, its frame from the hatch atlas (the hatchery's `hatch` texture
 *   slot: one row of nine 48 × 64 frames), white emissive 0.3, alpha-tested,
 *   lit by the room's radiance (`kit.finish`).
 * - The warm glow behind it in the glow phase (the prototype's soft disc).
 *
 * Changed from the prototype: the hatchling itself is not a card here; the
 * sprite layer draws it once the sequence has run (its species' sheet, or the
 * proxy). The prototype's queue of pending sequences per spot is the shell's
 * to keep (it starts one sequence at a time). One texture: the atlas is cloned
 * once and its offset picks the frame.
 */
import * as THREE from 'three';
import { type ShipKit, at, tag } from './kit.ts';

/** The sequence's timing (seconds) and frames: wobble, crack, glow, then appeared. */
export const hatchTiming = {
  duration: 4,
  wobbleUntil: 1,
  crackUntil: 2,
  /** Frames while wobbling (at `wobbleFps`), the first crack frame (two, at 2 a second), the first glow frame (four over the glow). */
  wobbleFrames: [0, 1, 0, 2],
  wobbleFps: 8,
  crackFrame: 3,
  glowFrame: 5,
  frames: 9,
} as const;

export type HatchPhase = 'idle' | 'wobble' | 'crack' | 'glow' | 'appeared';

export type HatchDisplay = {
  readonly group: THREE.Group;
  setAtlas(atlas: THREE.Texture): void;
  start(time: number): void;
  reset(): void;
  /** Draws the sequence at `time` (the event clock); `still` (reduced motion) holds the wobble's first frame. */
  update(time: number, camera: THREE.PerspectiveCamera, still: boolean): HatchPhase;
  /** The phase the last `update` drew. */
  phase(): HatchPhase;
  /** The sequence's length, seconds. */
  duration(): number;
  dispose(): void;
};

const glowVertex = 'varying vec2 uvGlow; void main(){uvGlow=uv; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);}';
const glowFragment = 'varying vec2 uvGlow; uniform vec3 tint; void main(){float a=1.-smoothstep(.08,.5,length(uvGlow-.5)); gl_FragColor=vec4(tint,a*.38);}';

export const createHatchDisplay = (kit: ShipKit, spot: { readonly x: number; readonly y: number; readonly z: number }): HatchDisplay => {
  const t = hatchTiming;
  const group = new THREE.Group();
  group.name = 'hatch-sequence';
  const eggGeometry = tag(new THREE.PlaneGeometry(1.2, 1.6));
  const eggMaterial = kit.finish(new THREE.MeshStandardMaterial({ emissive: '#ffffff', emissiveIntensity: 0.3, alphaTest: 0.45, side: THREE.DoubleSide, roughness: 1 }));
  eggMaterial.name = 'airship-hatch-card';
  const egg = new THREE.Mesh(eggGeometry, eggMaterial);
  egg.name = 'hatch-sequence:egg';
  egg.renderOrder = 1;
  egg.position.set(spot.x, spot.y + 0.8, spot.z);
  egg.visible = false;
  const glowGeometry = new THREE.PlaneGeometry(1.7, 1.7);
  const glowMaterial = new THREE.ShaderMaterial({
    uniforms: { tint: { value: new THREE.Color('#ffdb95') } },
    vertexShader: glowVertex,
    fragmentShader: glowFragment,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    forceSinglePass: true,
  });
  const glow = new THREE.Mesh(glowGeometry, glowMaterial);
  glow.name = 'hatch-sequence:glow';
  glow.position.copy(egg.position);
  glow.position.z -= 0.015;
  glow.visible = false;
  group.add(glow, egg);
  let frames: THREE.Texture | null = null;
  let started: number | null = null;
  let last: HatchPhase = 'idle';
  const show = (frame: number): void => {
    if (frames === null) return;
    frames.offset.x = frame / t.frames;
  };
  return {
    group,
    setAtlas(atlas) {
      frames?.dispose();
      const f = atlas.clone();
      f.colorSpace = THREE.SRGBColorSpace;
      f.magFilter = THREE.NearestFilter;
      f.minFilter = THREE.NearestFilter;
      f.generateMipmaps = false;
      f.repeat.set(1 / t.frames, 1);
      f.offset.set(0, 0);
      f.needsUpdate = true;
      frames = f;
      eggMaterial.map = f;
      eggMaterial.emissiveMap = f;
      eggMaterial.needsUpdate = true;
    },
    start(time) {
      started = time;
    },
    reset() {
      started = null;
      last = 'idle';
      egg.visible = false;
      glow.visible = false;
    },
    update(time, camera, still) {
      if (started === null) {
        egg.visible = false;
        glow.visible = false;
        last = 'idle';
        return last;
      }
      const elapsed = Math.max(0, time - started);
      const phase: HatchPhase = elapsed < t.wobbleUntil ? 'wobble' : elapsed < t.crackUntil ? 'crack' : elapsed < t.duration ? 'glow' : 'appeared';
      const active = phase !== 'appeared';
      if (phase === 'wobble') show(still ? at(t.wobbleFrames, 0) : at(t.wobbleFrames, Math.floor(elapsed * t.wobbleFps) % t.wobbleFrames.length));
      else if (phase === 'crack') show(t.crackFrame + Math.min(1, Math.floor((elapsed - t.wobbleUntil) * 2)));
      else if (phase === 'glow') show(t.glowFrame + Math.min(3, Math.floor(((elapsed - t.crackUntil) * 4) / Math.max(0.01, t.duration - t.crackUntil))));
      egg.visible = active && frames !== null;
      glow.visible = phase === 'glow' && frames !== null;
      egg.quaternion.copy(camera.quaternion);
      glow.quaternion.copy(camera.quaternion);
      last = phase;
      return phase;
    },
    phase: () => last,
    duration: () => t.duration,
    dispose() {
      eggGeometry.dispose();
      eggMaterial.dispose();
      glowGeometry.dispose();
      glowMaterial.dispose();
      frames?.dispose();
    },
  };
};
